import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import * as inboxApi from '../../api/inbox';
import InboxAnalyticsCard from './InboxAnalyticsCard';
import ReadRosterModal from './ReadRosterModal';
import { txt } from '../../utils/bilingualText';

/**
 * TabInbox - Internal Inbox System
 * Unified inbox for system notifications, approvals, tasks, and messages
 */
const TabInbox = () => {
  const { user, isAdmin, isManagement, isSupervisor } = useAuth();
  
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unreadCounts, setUnreadCounts] = useState({ total: 0, by_type: {}, by_status: {} });
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalThreads, setTotalThreads] = useState(0);
  const [selectedThreadIds, setSelectedThreadIds] = useState([]);
  const [showAnalytics, setShowAnalytics] = useState(false);
  
  const canSendMessages = isAdmin() || isManagement() || isSupervisor();
  const canViewAnalytics = isAdmin() || isManagement();

  // Polling for unread counts (every 30s)
  useEffect(() => {
    loadUnreadCounts();
    const interval = setInterval(loadUnreadCounts, 30000); // 30s
    return () => clearInterval(interval);
  }, []);

  // Load threads when filter changes
  useEffect(() => {
    loadThreads();
    // Scroll to top when filter changes
    const messageList = document.querySelector('.inbox-message-list');
    if (messageList) {
      messageList.scrollTop = 0;
    }
  }, [activeFilter, searchQuery, page]);

  const loadUnreadCounts = async () => {
    try {
      const counts = await inboxApi.getUnreadCounts();
      setUnreadCounts(counts);
    } catch (error) {
      console.error('Failed to load unread counts:', error);
    }
  };

  const loadThreads = async () => {
    setLoading(true);
    try {
      const filters = {
        page,
        limit: 50
      };
      
      if (activeFilter !== 'all') {
        if (activeFilter === 'unread') {
          filters.status = 'unread';
        } else {
          filters.type = activeFilter;
        }
      }
      
      if (searchQuery) {
        filters.search = searchQuery;
      }
      
      const response = await inboxApi.getInboxThreads(filters);
      setThreads(response.threads);
      setTotalThreads(response.total);
    } catch (error) {
      console.error('Failed to load threads:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleThreadClick = async (thread) => {
    setSelectedThread(thread);
    
    // Mark as read if unread
    if (thread.is_unread) {
      try {
        await inboxApi.markAsRead([thread.thread_id]);
        await loadThreads();
        await loadUnreadCounts();
      } catch (error) {
        console.error('Failed to mark as read:', error);
      }
    }
  };

  const handleMarkAllRead = async () => {
    try {
      const unreadThreadIds = threads.filter(t => t.is_unread).map(t => t.thread_id);
      if (unreadThreadIds.length > 0) {
        await inboxApi.markAsRead(unreadThreadIds);
        await loadThreads();
        await loadUnreadCounts();
      }
    } catch (error) {
      console.error('Failed to mark all as read:', error);
      alert('Failed to mark all as read');
    }
  };

  const handleBulkMarkRead = async () => {
    if (selectedThreadIds.length === 0) return;
    
    try {
      await inboxApi.markAsRead(selectedThreadIds);
      setSelectedThreadIds([]);
      await loadThreads();
      await loadUnreadCounts();
    } catch (error) {
      console.error('Failed to bulk mark read:', error);
      alert('Failed to mark selected as read');
    }
  };

  const handleSelectAll = () => {
    if (selectedThreadIds.length === threads.length) {
      setSelectedThreadIds([]);
    } else {
      setSelectedThreadIds(threads.map(t => t.thread_id));
    }
  };

  const handleSelectThread = (threadId) => {
    setSelectedThreadIds(prev => 
      prev.includes(threadId)
        ? prev.filter(id => id !== threadId)
        : [...prev, threadId]
    );
  };

  const handleApprove = async (threadId, notes) => {
    try {
      await inboxApi.approveRequest(threadId, notes);
      alert('✅ Approved successfully');
      setSelectedThread(null);
      await loadThreads();
      await loadUnreadCounts();
    } catch (error) {
      console.error('Failed to approve:', error);
      alert('Failed to approve request');
    }
  };

  const handleRequestChanges = async (threadId, notes) => {
    if (!notes) {
      alert('Please provide feedback for the changes requested');
      return;
    }
    
    try {
      await inboxApi.requestChanges(threadId, notes);
      alert('🔄 Changes requested successfully');
      setSelectedThread(null);
      await loadThreads();
      await loadUnreadCounts();
    } catch (error) {
      console.error('Failed to request changes:', error);
      alert('Failed to request changes');
    }
  };

  const handleResolve = async (threadId) => {
    try {
      await inboxApi.resolveThread(threadId);
      alert('✅ Thread resolved');
      setSelectedThread(null);
      await loadThreads();
      await loadUnreadCounts();
    } catch (error) {
      console.error('Failed to resolve:', error);
      alert('Failed to resolve thread');
    }
  };

  const getTypeIcon = (type) => {
    const icons = {
      system: '💬',
      manual: '✉️',
      approval: '✅',
      announcement: '📢',
      reminder: '⏰',
      task: '📋'
    };
    return icons[type] || '💬';
  };

  const getTypeColor = (type) => {
    const colors = {
      system: 'bg-blue-50 text-blue-700 border-blue-200',
      manual: 'bg-gray-50 text-gray-700 border-gray-200',
      approval: 'bg-green-50 text-green-700 border-green-200',
      announcement: 'bg-purple-50 text-purple-700 border-purple-200',
      reminder: 'bg-yellow-50 text-yellow-700 border-yellow-200',
      task: 'bg-orange-50 text-orange-700 border-orange-200'
    };
    return colors[type] || 'bg-gray-50 text-gray-700 border-gray-200';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div 
      className="h-full flex flex-col overflow-hidden bg-gray-50"
      style={{
        minHeight: '-webkit-fill-available'
      }}
    >
      {/* Compact Toolbar */}
      <div className="bg-white border-b border-gray-200 px-4 py-2 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {unreadCounts.total} unread {unreadCounts.total === 1 ? 'message' : 'messages'}
          </div>
          <div className="flex gap-2">
            {canViewAnalytics && (
              <button
                onClick={() => setShowAnalytics(!showAnalytics)}
                className="px-2 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700"
              >
                📊
              </button>
            )}
            {threads.some(t => t.is_unread) && (
              <button
                onClick={handleMarkAllRead}
                className="px-2.5 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                ✓ Mark All Read
              </button>
            )}
          </div>
        </div>

        {/* Analytics Card */}
        {showAnalytics && canViewAnalytics && (
          <div className="mt-2">
            <InboxAnalyticsCard />
          </div>
        )}

        {/* Bulk Actions Toolbar */}
        {selectedThreadIds.length > 0 && (
          <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded flex items-center justify-between">
            <div className="text-xs text-blue-900 font-medium">
              {selectedThreadIds.length} selected
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleBulkMarkRead}
                className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
              >
                ✓ Mark Read
              </button>
              <button
                onClick={() => setSelectedThreadIds([])}
                className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded hover:bg-gray-300"
              >
                Clear
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Sticky Filter Tabs - Compact Pills */}
      <div className="bg-white border-b border-gray-200 px-4 py-2 flex-shrink-0 sticky top-0 z-10">
        <div className="flex gap-2 overflow-x-auto scrollbar-hide mb-2">
          {[
            { id: 'all', label: 'All', count: totalThreads },
            { id: 'unread', label: 'Unread', count: unreadCounts.total },
            { id: 'approval', label: 'Approvals', count: unreadCounts.by_type.approval || 0 },
            { id: 'system', label: 'System', count: unreadCounts.by_type.system || 0 },
            { id: 'task', label: 'Tasks', count: unreadCounts.by_type.task || 0 },
            { id: 'reminder', label: 'Reminders', count: unreadCounts.by_type.reminder || 0 }
          ].map(filter => (
            <button
              key={filter.id}
              onClick={() => { setActiveFilter(filter.id); setPage(1); }}
              style={{
                height: '34px',
                fontSize: '13px'
              }}
              className={`
                px-3 py-1.5 rounded-full font-medium whitespace-nowrap transition-all
                ${activeFilter === filter.id
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                }
              `}
            >
              {filter.label}
              {filter.count > 0 && (
                <span className={`ml-1.5 px-1.5 py-0.5 text-xs rounded-full ${
                  activeFilter === filter.id ? 'bg-white text-blue-600' : 'bg-white text-gray-700'
                }`}>
                  {filter.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Search - Compact */}
        <input
          type="text"
          placeholder="Search messages..."
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
          className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          style={{ fontSize: '13px' }}
        />
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-hidden flex">
        {/* Thread List - Scrollable with iOS fixes */}
        <div 
          className={`${selectedThread ? 'hidden sm:block' : 'block'} w-full sm:w-96 border-r border-gray-200 bg-white inbox-message-list`}
          style={{
            maxHeight: 'calc(100vh - 260px)',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            scrollBehavior: 'smooth',
            overscrollBehavior: 'contain'
          }}
        >
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500">Loading...</div>
            </div>
          ) : threads.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <span className="text-4xl mb-2">📭</span>
              <p>No messages</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {/* Select All Header */}
              {threads.length > 0 && (
                <div className="p-3 bg-gray-50 border-b border-gray-200 flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={selectedThreadIds.length === threads.length}
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <span className="text-sm text-gray-600">Select all</span>
                </div>
              )}
              
              {threads.map(thread => (
                <div
                  key={thread.thread_id}
                  className={`
                    hover:bg-gray-50 transition-colors
                    ${thread.is_unread ? 'bg-blue-50' : ''}
                    ${selectedThread?.thread_id === thread.thread_id ? 'bg-gray-100' : ''}
                  `}
                  style={{ padding: '10px 12px' }}
                >
                  <div className="flex items-start gap-2.5">
                    <input
                      type="checkbox"
                      checked={selectedThreadIds.includes(thread.thread_id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleSelectThread(thread.thread_id);
                      }}
                      className="w-4 h-4 text-blue-600 rounded mt-0.5"
                    />
                    <span 
                      className="text-xl flex-shrink-0 cursor-pointer"
                      onClick={() => handleThreadClick(thread)}
                    >
                      {getTypeIcon(thread.type)}
                    </span>
                    <div 
                      className="flex-1 min-w-0 cursor-pointer"
                      onClick={() => handleThreadClick(thread)}
                    >
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <h3 
                          className={`font-semibold truncate ${thread.is_unread ? 'text-gray-900' : 'text-gray-700'}`}
                          style={{ fontSize: '15px', lineHeight: '1.4' }}
                        >
                          {thread.subject}
                        </h3>
                        {thread.is_unread && (
                          <span className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0"></span>
                        )}
                      </div>
                      <p 
                        className="text-gray-600 truncate"
                        style={{ fontSize: '13px', lineHeight: '1.4', marginBottom: '6px' }}
                      >
                        {thread.message}
                      </p>
                      <div className="flex items-center justify-between">
                        <span 
                          className={`px-2 py-0.5 rounded-full ${getTypeColor(thread.type)}`}
                          style={{ fontSize: '11px' }}
                        >
                          {thread.type}
                        </span>
                        <span 
                          className="text-gray-500"
                          style={{ fontSize: '12px' }}
                        >
                          {formatDate(thread.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Thread Detail - Scrollable */}
        <div 
          className={`${selectedThread ? 'block' : 'hidden sm:block'} flex-1 bg-white`}
          style={{
            maxHeight: 'calc(100vh - 260px)',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            scrollBehavior: 'smooth',
            overscrollBehavior: 'contain'
          }}
        >
          {selectedThread ? (
            <ThreadDetail
              thread={selectedThread}
              onClose={() => setSelectedThread(null)}
              onApprove={handleApprove}
              onRequestChanges={handleRequestChanges}
              onResolve={handleResolve}
              canApprove={canSendMessages}
              userRole={user?.role}
            />
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              <div className="text-center">
                <span className="text-6xl">📬</span>
                <p className="mt-4">Select a message to read</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Thread Detail Component
const ThreadDetail = ({ thread, onClose, onApprove, onRequestChanges, onResolve, canApprove, userRole }) => {
  const [notes, setNotes] = useState('');
  const [showApprovalActions, setShowApprovalActions] = useState(false);
  const [action, setAction] = useState(null); // 'approve' or 'reject'

  const handleAction = () => {
    if (action === 'approve') {
      onApprove(thread.thread_id, notes || null);
    } else if (action === 'reject') {
      onRequestChanges(thread.thread_id, notes);
    }
    setShowApprovalActions(false);
    setNotes('');
    setAction(null);
  };

  const formatFullDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const canSeeAction = (actionItem) => {
    if (!actionItem.visible_for || actionItem.visible_for.length === 0) return true;
    return actionItem.visible_for.includes(userRole);
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-4 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            className="sm:hidden text-gray-600 hover:text-gray-900"
          >
            ← Back
          </button>
          <span className="text-2xl">{thread.type === 'system' ? '💬' : thread.type === 'approval' ? '✅' : thread.type === 'task' ? '📋' : '✉️'}</span>
          <div>
            <h2 className="text-lg font-bold text-gray-900">{thread.subject}</h2>
            <p className="text-xs text-gray-500">
              From: {thread.from_user_name || 'System'} • {formatFullDate(thread.created_at)}
            </p>
          </div>
        </div>
        {thread.status !== 'resolved' && (
          <button
            onClick={() => onResolve(thread.thread_id)}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            ✓ Resolve
          </button>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="prose max-w-none">
          <p className="whitespace-pre-wrap text-gray-700">{thread.message}</p>
        </div>

        {/* Related Item */}
        {thread.related_item && (
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="text-sm font-semibold text-blue-900 mb-2">Related Item</h3>
            <p className="text-sm text-blue-800">
              <strong>{thread.related_item.type}:</strong> {txt(thread.related_item.name)}
            </p>
            {thread.related_item.link && (
              <a
                href={thread.related_item.link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:underline mt-2 inline-block"
              >
                View Item →
              </a>
            )}
          </div>
        )}

        {/* Actions */}
        {thread.actions && thread.actions.length > 0 && thread.status !== 'resolved' && (
          <div className="mt-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Actions</h3>
            <div className="flex flex-wrap gap-2">
              {thread.actions.filter(canSeeAction).map((actionItem, idx) => {
                if (actionItem.type === 'approve' && canApprove) {
                  return (
                    <button
                      key={`item-${idx}`}
                      onClick={() => { setAction('approve'); setShowApprovalActions(true); }}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
                    >
                      ✓ {actionItem.label}
                    </button>
                  );
                } else if (actionItem.type === 'reject' && canApprove) {
                  return (
                    <button
                      key={`item-${idx}`}
                      onClick={() => { setAction('reject'); setShowApprovalActions(true); }}
                      className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium"
                    >
                      🔄 {actionItem.label}
                    </button>
                  );
                } else if (actionItem.type === 'view' && actionItem.link) {
                  return (
                    <a
                      key={`item-${idx}`}
                      href={actionItem.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium inline-block"
                    >
                      👁️ {actionItem.label}
                    </a>
                  );
                } else if (actionItem.type === 'acknowledge') {
                  return (
                    <button
                      key={`item-${idx}`}
                      onClick={() => onResolve(thread.thread_id)}
                      className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium"
                    >
                      ✓ {actionItem.label}
                    </button>
                  );
                }
                return null;
              })}
            </div>

            {/* Approval Action Form */}
            {showApprovalActions && (
              <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">
                  {action === 'approve' ? 'Approve Request' : 'Request Changes'}
                </h4>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={action === 'approve' ? 'Optional notes...' : 'Required: Please provide feedback...'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                  rows={3}
                />
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={handleAction}
                    className={`px-4 py-2 rounded-lg text-white font-medium ${
                      action === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-orange-600 hover:bg-orange-700'
                    }`}
                  >
                    {action === 'approve' ? '✓ Approve' : '🔄 Request Changes'}
                  </button>
                  <button
                    onClick={() => { setShowApprovalActions(false); setNotes(''); setAction(null); }}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Audit Trail */}
        {thread.audit && thread.audit.length > 0 && (
          <div className="mt-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Activity</h3>
            <div className="space-y-2">
              {thread.audit.map((event, idx) => (
                <div key={`item-${idx}`} className="text-xs text-gray-600 flex items-start gap-2">
                  <span className="text-blue-600">•</span>
                  <div>
                    <strong>{event.event}</strong> by {event.actor} 
                    <span className="text-gray-500"> • {formatFullDate(event.timestamp)}</span>
                    {event.notes && <p className="mt-1 text-gray-700">{event.notes}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TabInbox;
